import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  DatePicker,
  Drawer,
  Empty,
  Space,
  Spin,
  Statistic,
  Tag,
  Timeline,
  Tooltip,
  Typography,
} from "antd";
import {
  CarOutlined,
  CompassOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  ReloadOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import axios from "../../services/axiosInstance";
import { TRACKING_DRIVER_TRAIL } from "../../services/apiEndPoints";
import {
  ILocationTrace,
  IRakeDriverWithLatest,
} from "../types/tracking.types";
import DriverTrailMap from "./DriverTrailMap";
import { toastError } from "../../utils/toast";

dayjs.extend(relativeTime);

const { Text, Title } = Typography;

interface DriverTrailDrawerProps {
  open: boolean;
  rakeMasterId: string | null;
  driver: IRakeDriverWithLatest | null;
  onClose: () => void;
}

const GRANTED_CONSENT_STATUSES = new Set([
  "consent_approved",
  "approved",
  "granted",
  "given",
  "allowed",
  "accepted",
  "active",
  "yes",
  "true",
]);

const isConsentGranted = (status?: string | null) => {
  if (!status) return false;
  return GRANTED_CONSENT_STATUSES.has(String(status).toLowerCase());
};

const consentTag = (status?: string | null) => {
  if (!status) {
    return <Tag color="default">Consent unknown</Tag>;
  }
  const lower = String(status).toLowerCase();
  if (GRANTED_CONSENT_STATUSES.has(lower)) {
    return <Tag color="success">Consent {status}</Tag>;
  }
  if (lower === "pending") {
    return <Tag color="warning">Consent pending</Tag>;
  }
  return <Tag color="error">Consent {status}</Tag>;
};

const DriverTrailDrawer = ({
  open,
  rakeMasterId,
  driver,
  onClose,
}: DriverTrailDrawerProps) => {
  const [date, setDate] = useState<Dayjs>(dayjs());
  const [loading, setLoading] = useState(false);
  const [traces, setTraces] = useState<ILocationTrace[]>([]);

  const consentGranted = isConsentGranted(driver?.consentStatus);

  const fetchTrail = useCallback(async () => {
    if (!rakeMasterId || !driver) return;
    if (!consentGranted) {
      setTraces([]);
      return;
    }
    setLoading(true);
    try {
      const since = date.startOf("day").toISOString();
      const until = date.endOf("day").toISOString();
      const url = TRACKING_DRIVER_TRAIL(rakeMasterId, driver.rakeDriverId);
      const res = await axios.get(url, {
        params: { since, until, limit: 1000 },
      });
      const results: ILocationTrace[] = res?.data?.results ?? [];
      setTraces(results);
    } catch {
      toastError("Failed to load location trail");
      setTraces([]);
    } finally {
      setLoading(false);
    }
  }, [rakeMasterId, driver, date, consentGranted]);

  useEffect(() => {
    if (open) {
      setDate(dayjs());
    }
  }, [open, driver?.rakeDriverId]);

  useEffect(() => {
    if (open && driver && consentGranted) {
      fetchTrail();
    } else if (open && !consentGranted) {
      setTraces([]);
    }
  }, [open, driver, consentGranted, fetchTrail]);

  const stats = useMemo(() => {
    if (traces.length === 0) {
      return { points: 0, distance: 0, lastSpeed: 0 };
    }
    const totalDistance = traces.reduce(
      (sum, t) => sum + Number(t.distance || 0),
      0
    );
    const last = traces[traces.length - 1];
    return {
      points: traces.length,
      distance: totalDistance,
      lastSpeed: Number(last.speed || 0),
    };
  }, [traces]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={Math.min(window.innerWidth - 40, 720)}
      title={
        driver ? (
          <Space size={10} align="center">
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: "linear-gradient(135deg, #1677ff, #4096ff)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              {driver.name?.[0]?.toUpperCase() || "D"}
            </div>
            <div>
              <Text strong style={{ fontSize: 15 }}>
                {driver.name}
              </Text>
              <div style={{ fontSize: 11, color: "#64748b" }}>
                <PhoneOutlined style={{ marginRight: 4 }} />
                {driver.phoneNumber}
                {driver.truckNumber && (
                  <>
                    {" · "}
                    <CarOutlined style={{ marginRight: 4 }} />
                    {driver.truckNumber}
                  </>
                )}
              </div>
            </div>
          </Space>
        ) : (
          "Driver details"
        )
      }
      extra={
        <Space>
          <DatePicker
            value={date}
            onChange={(d) => d && setDate(d)}
            allowClear={false}
            disabled={!consentGranted}
          />
          <Tooltip title="Refresh">
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchTrail}
              disabled={!consentGranted}
              loading={loading}
            />
          </Tooltip>
        </Space>
      }
    >
      {!driver ? null : !consentGranted ? (
        <Alert
          showIcon
          type="warning"
          message="Driver consent not given"
          description={
            driver.consentStatus
              ? `The driver's tracking consent is currently "${driver.consentStatus}". Location history is unavailable until consent is approved.`
              : "The driver has not yet shared their tracking consent. Location history is unavailable until consent is approved."
          }
          style={{ marginBottom: 16 }}
        />
      ) : (
        <>
          <Space size={8} wrap style={{ marginBottom: 12 }}>
            {consentTag(driver.consentStatus)}
            {driver.isTracked ? (
              <Tag icon={<SyncOutlined spin />} color="processing">
                Tracking active
              </Tag>
            ) : (
              <Tag color="default">Not currently tracked</Tag>
            )}
            {driver.lastSyncedAt && (
              <Tag color="blue">
                Last synced {dayjs(driver.lastSyncedAt).fromNow()}
              </Tag>
            )}
          </Space>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 12,
              marginBottom: 16,
            }}
          >
            <div
              style={{
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 10,
                padding: "10px 12px",
              }}
            >
              <Statistic
                title="Trace points"
                value={stats.points}
                valueStyle={{ fontSize: 18, color: "#1677ff" }}
              />
            </div>
            <div
              style={{
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 10,
                padding: "10px 12px",
              }}
            >
              <Statistic
                title="Distance"
                value={stats.distance.toFixed(2)}
                suffix="km"
                valueStyle={{ fontSize: 18, color: "#10b981" }}
              />
            </div>
            <div
              style={{
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 10,
                padding: "10px 12px",
              }}
            >
              <Statistic
                title="Latest speed"
                value={stats.lastSpeed.toFixed(1)}
                suffix="km/h"
                valueStyle={{ fontSize: 18, color: "#8b5cf6" }}
              />
            </div>
          </div>

          {loading ? (
            <div style={{ textAlign: "center", padding: 60 }}>
              <Spin />
              <div style={{ marginTop: 12 }}>
                <Text type="secondary">Loading location trail...</Text>
              </div>
            </div>
          ) : traces.length === 0 ? (
            <Empty description={`No location traces on ${date.format("MMM D, YYYY")}`} />
          ) : (
            <>
              <Title level={5} style={{ margin: "8px 0 10px" }}>
                <EnvironmentOutlined style={{ marginRight: 6, color: "#1677ff" }} />
                Map view
              </Title>
              <DriverTrailMap traces={traces} height={320} />

              <Title level={5} style={{ margin: "8px 0 10px" }}>
                <CompassOutlined style={{ marginRight: 6, color: "#1677ff" }} />
                Timeline
              </Title>
              <Timeline
                style={{ paddingLeft: 4 }}
                items={[...traces].reverse().map((t, idx) => ({
                  color:
                    t.tripStatus === "STOPPED"
                      ? "gray"
                      : idx === 0
                      ? "green"
                      : "blue",
                  children: (
                    <div>
                      <Space size={8} wrap>
                        <Text strong style={{ fontSize: 13 }}>
                          {dayjs(t.timestamp).format("h:mm:ss A")}
                        </Text>
                        {t.tripStatus && (
                          <Tag color="geekblue" style={{ margin: 0 }}>
                            {t.tripStatus}
                          </Tag>
                        )}
                        {typeof t.speed === "number" && (
                          <Tag color="purple" style={{ margin: 0 }}>
                            {t.speed.toFixed(1)} km/h
                          </Tag>
                        )}
                      </Space>
                      <div
                        style={{
                          fontSize: 12,
                          color: "#475569",
                          marginTop: 4,
                        }}
                      >
                        {t.detailedAddress || (
                          <span>
                            {t.latitude.toFixed(5)}, {t.longitude.toFixed(5)}
                          </span>
                        )}
                      </div>
                      {t.statusText && (
                        <div
                          style={{
                            fontSize: 11,
                            color: "#94a3b8",
                            marginTop: 2,
                          }}
                        >
                          {t.statusText}
                        </div>
                      )}
                    </div>
                  ),
                }))}
              />
            </>
          )}
        </>
      )}
    </Drawer>
  );
};

export default DriverTrailDrawer;
